import React, { CSSProperties, useEffect } from 'react'
import { DateTime } from 'luxon'
import { List } from 'semantic-ui-react'
import { fetchAdminAllQuarterlyTaxEstimateDetailsIfNeeded } from '../../quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectQuarterlyTaxEstimateDetailsByYearAndQuarter } from '../../quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'

interface Props {
  calendarQuarter: string
  calendarYear: string
  style?: CSSProperties
}

const formatDate = (isoDate: string | undefined) =>
  isoDate
    ? DateTime.fromISO(isoDate, { zone: 'utc' }).toFormat(
        DATE_FORMATS_LUXON.DISPLAY_SHORT
      )
    : '--'

const QTEDateDetails: React.FC<Props> = ({
  calendarQuarter,
  calendarYear,
  style,
}) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchAdminAllQuarterlyTaxEstimateDetailsIfNeeded())
  }, [dispatch])

  const details = useReselector(
    selectQuarterlyTaxEstimateDetailsByYearAndQuarter,
    calendarYear,
    calendarQuarter
  )

  if (!details) {
    return (
      <div>
        <i>
          Date details do not currently exist for {calendarYear}{' '}
          {calendarQuarter}
        </i>
      </div>
    )
  }
  return (
    <List relaxed="very" divided verticalAlign="middle" style={style}>
      <List.Item>
        <List.Content floated="right">
          {`${formatDate(details?.bookkeepingPeriodStartsAt)} - ${formatDate(details?.bookkeepingPeriodEndsAt)}`}
        </List.Content>
        <List.Header>Bookkeeping Period</List.Header>
      </List.Item>
      <List.Item>
        <List.Content floated="right">
          {formatDate(details?.newUserCutOffAt)}
        </List.Content>
        <List.Header>New User Cut-Off</List.Header>
      </List.Item>
      <List.Item>
        <List.Content floated="right">
          {`${formatDate(details?.newUserCutOffAt)} - ${
            details?.calculationStartsAt
              ? DateTime.fromISO(details.calculationStartsAt, { zone: 'utc' })
                  .minus({ days: 1 })
                  .toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)
              : '--'
          }`}
        </List.Content>
        <List.Header>Profile Review Period</List.Header>
      </List.Item>
      <List.Item>
        <List.Content floated="right">
          {`${formatDate(details?.calculationStartsAt)} - ${
            details?.clientNotifiedAt
              ? DateTime.fromISO(details.clientNotifiedAt, { zone: 'utc' })
                  .minus({ days: 1 })
                  .toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)
              : '--'
          }`}
        </List.Content>
        <List.Header>Calculate, Review, Upload Period</List.Header>
      </List.Item>
      <List.Item>
        <List.Content floated="right">
          {formatDate(details?.clientNotifiedAt)}
        </List.Content>
        <List.Header>Notify Customers</List.Header>
      </List.Item>
      <List.Item>
        <List.Content floated="right">
          {formatDate(details?.irsPaymentDueAt)}
        </List.Content>
        <List.Header>IRS Payment</List.Header>
      </List.Item>
      <List.Item>
        <List.Content floated="right">
          {details?.irsPaymentDueAt
            ? DateTime.fromISO(details.irsPaymentDueAt, { zone: 'utc' })
                .plus({ days: 1 })
                .toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)
            : '--'}
        </List.Content>
        <List.Header>Tax Information Unlocked</List.Header>
      </List.Item>
    </List>
  )
}

export default QTEDateDetails
